import type { ReactNode } from 'react';
import {
  Dialog as AriaDialog,
  Heading,
  Modal,
  ModalOverlay,
  type ModalOverlayProps,
} from 'react-aria-components';

import styles from './Dialog.module.css';

export { DialogTrigger } from 'react-aria-components';

export interface DialogProps extends Omit<ModalOverlayProps, 'children' | 'className' | 'style'> {
  title: string;
  /**
   * `alert` is for blocking safety stops and destructive confirmations: danger heading and
   * rule, `alertdialog` role, and no dismissing by clicking outside.
   */
  tone?: 'default' | 'alert';
  children: ReactNode | ((opts: { close: () => void }) => ReactNode);
}

/**
 * A modal dialog. Use inside `DialogTrigger` with a `Button`, or control it with
 * `isOpen` and `onOpenChange`. Escape closes it and focus returns to the trigger.
 */
export function Dialog({ title, tone = 'default', children, isDismissable, ...rest }: DialogProps) {
  return (
    <ModalOverlay
      {...rest}
      isDismissable={tone === 'alert' ? false : (isDismissable ?? true)}
      className={styles.overlay}
    >
      <Modal className={styles.modal}>
        <AriaDialog
          className={styles.dialog}
          data-tone={tone}
          role={tone === 'alert' ? 'alertdialog' : 'dialog'}
        >
          {({ close }) => (
            <>
              <Heading slot="title" className={styles.title}>
                {title}
              </Heading>
              <div className={styles.body}>
                {typeof children === 'function' ? children({ close }) : children}
              </div>
            </>
          )}
        </AriaDialog>
      </Modal>
    </ModalOverlay>
  );
}

/** Right-aligned row of dialog buttons. Put the safe action last, as the primary. */
export function DialogActions({ children }: { children: ReactNode }) {
  return <div className={styles.actions}>{children}</div>;
}
